<template>
  <v-sheet
    class="mb-5 mt-n8 align-center d-flex flex-row pa-1"
    color="appBackground darken-1"
    rounded="pill"
  >
    <v-menu
      :close-on-content-click="false"
      ref="menuRef"
      min-width="250"
      max-width="300"
      absolute
      right
      offset-x
      offset-y
    >
      <template v-slot:activator="{ on: menu, attrs }">
        <v-btn icon @click.stop="clearSelection" class="order-1">
          <v-icon color="defaultIconColor">mdi-close</v-icon>
        </v-btn>

        <span class="order-2">
          {{ count }} {{ $t('selected') }}
        </span>

        <span v-on="menu" class="order-3 ml-5">
          <v-tooltip top>
            <template v-slot:activator="{on: tooltip}">
              <v-btn
                v-show="!isSomeItemLocked && canCopy"
                icon
                @click="setActionType('copyAction')"
                v-on="tooltip"
                v-bind="attrs"
              >
                <v-icon color="defaultIconColor">mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('copy') }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{on: tooltip}">
              <v-btn
                v-show="!isSomeItemLocked && canMoveOrDelete"
                icon
                @click="setActionType('moveAction')"
                v-on="tooltip"
                v-bind="attrs"
              >
                <v-icon color="defaultIconColor">mdi-folder-move-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('move') }}</span>
          </v-tooltip>
        </span>

        <v-tooltip top>
          <template v-slot:activator="{on: tooltip}">
            <v-btn 
              v-show="canMoveOrDelete"
              icon 
              @click.stop="removeAction($event)" 
              v-on="tooltip" 
              class="order-4"
            >
              <v-icon color="defaultIconColor">mdi-delete-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('remove') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{on: tooltip}">
            <v-btn
              icon
              @click.stop="downloadAction"
              v-on="tooltip"
              class="order-5"
            >
              <v-icon color="defaultIconColor">mdi-download-multiple</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('download') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{on: tooltip}">
            <v-btn
              icon
              @click.stop="openShareModal"
              v-on="tooltip"
              class="order-6"
            >
              <v-icon color="defaultIconColor">mdi-share-all</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('share') }}</span>
        </v-tooltip>
      </template>

      <NavigationModal
        :node="selectedItems && selectedItems.length > 0 ? selectedItems[0] : undefined"
        :submit-title="actionType === 'copyAction' ? $t('copy') : $t('move')"
        :action-type="actionType"
        ref="navigationModal"
        :selected-items="selectedItems"
        @chosen-directory="callActionMethod(actionType, $event)"
      />
    </v-menu>

    <DefaultModal
      :title="$t('remove')"
      ref="modalRemove"
      @submit="removeExecute"
      submit-button-text="remove"
      deleteModal
    >
      <template v-slot:content>
        <v-col class="text-body-1">
          <v-row>
            <p class="pre-line"><span class="font-weight-bold">Files:</span> {{ filesListString }}</p>
          </v-row>
        </v-col>
      </template>
    </DefaultModal>

    <ShareModal
      ref="shareModal"
      :show-download-link="false"
      :title="$t('share')"
      @share-submitted="shareAction"
    />
  </v-sheet>
</template>

<script>

import { mapActions, mapState } from 'vuex'
import ContentActions from '@/store/content/actions/actions-types'
import NavigationModal from '@/views/content/navigation/NavigationModal.vue'
import AppActions from '@/store/app/actions-types'
import DefaultModal from '@/components/DefaultModal.vue'
import ShareModal from '@/components/content/ShareModal.vue'
import { BgActionEventBus } from '@/bgActionEventBus'
import NotificationActions from '@/store/core/notification/actions-types'

export default {
  name: "SelectedItemsBar",

  components: {
    NavigationModal,
    DefaultModal,
    ShareModal,
  },

  data() {
    return {
      actionType: null,
      pendingDownloadActions: new Set(),
      pendingShareActions: new Map(),
      bgActionHandler: null,
    }
  },

  created() {
    this.bgActionHandler = this.handleDownloadBgAction
    BgActionEventBus.$on('bgAction', this.bgActionHandler)
  },

  beforeDestroy() {
    if (this.bgActionHandler) {
      BgActionEventBus.$off('bgAction', this.bgActionHandler)
    }
  },

  props: {
    selectedItems: {
      type: Array,
      default: () => []
    },
    parentId: {
      type: String,
      default: null
    }
  },

  computed: {
    ...mapState({
      breadcrumb: state => state.content.entry.breadcrumb,
    }),

    count() {
      return this.selectedItems.length;
    },

    filesListString() {
      return this.selectedItems.map((e) => e.name).join(',\n')
    },

    isSomeItemLocked() {
      return this.selectedItems.some(item => item.locked === true);
    },

    hasAnyLockedItems() {
      return this.selectedItems.some(item => this.hasLock(item));
    },

    canCopy() {
      // Allow copy only if no items have exclusive or parent locks
      return !this.selectedItems.some(item => this.hasBlockingLock(item));
    },

    canMoveOrDelete() {
      // Block move/delete if any item has any type of lock
      return !this.hasAnyLockedItems;
    }
  },

  methods: {
    hasLock(item) {
      if (!item.locks) return false;
      
      let locksArray = [];
      if (Array.isArray(item.locks)) {
        locksArray = item.locks;
      } else if (typeof item.locks === 'object') {
        if ('mode' in item.locks) {
          locksArray = [item.locks];
        } else {
          locksArray = Object.values(item.locks).filter(v => v && typeof v === 'object' && 'mode' in v);
        }
      }
      
      return locksArray.length > 0;
    },

    hasBlockingLock(item) {
      // Exclusive and parent locks block copy operations
      if (!item.locks) return false;
      
      let locksArray = [];
      if (Array.isArray(item.locks)) {
        locksArray = item.locks;
      } else if (typeof item.locks === 'object') {
        if ('mode' in item.locks) {
          locksArray = [item.locks];
        } else {
          locksArray = Object.values(item.locks).filter(v => v && typeof v === 'object' && 'mode' in v);
        }
      }
      
      return locksArray.some(lock => lock.mode === 'exclusive' || lock.mode === 'parent');
    },

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE,
    ]),

    ...mapActions('content/actions', [
      ContentActions.REMOVE,
      ContentActions.MOVE,
      ContentActions.MOVE_ENTRIES,
      ContentActions.COPY,
      ContentActions.COPY_ENTRIES,
      ContentActions.REMOVE_ENTRIES,
      ContentActions.SHARE_MULTI_NODE_DOWNLOAD,
    ]),

    ...mapActions('core/notification', [
      NotificationActions.SEND_SHARE_MESSAGE,
    ]),

    setActionType(type) {
      this.actionType = type
    },

    clearSelection() {
      this.$emit("clear-selection")
    },

    callActionMethod(actionType, destinationNodeId) {
      this.clearSelection()
      this.$refs.navigationModal.closeModal()
      this.$refs.menuRef.isActive = false

      const ACTION =
        actionType === 'copyAction'
        ? ContentActions.COPY :
        actionType === 'moveAction'
        ? ContentActions.MOVE : null

      if (!ACTION) {
        return
      }

      if (ACTION == 'move') {
        this.moveAction(destinationNodeId)
        return
      }

      if (ACTION == 'copy') {
        this.copyAction(destinationNodeId)
        return
      }
    },

    copyAction(destinationNodeId) {
      let nodesIds = this.selectedItems.map(item => item.node_id)
      let params = {'entries': nodesIds, 'destination_id': destinationNodeId}

      this[ContentActions.COPY_ENTRIES](params)
        .then(() => {
          this.$emit('copy-success')
          this.clearSelection()
        })
        .catch((error) => {
          this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('copy_failed'))
          this.$emit('copy-error', error)
        })
    },

    moveAction(destinationNodeId) {
      let nodesIds = this.selectedItems.map(item => item.node_id)
      let params = {'entries': nodesIds, 'destination_id': destinationNodeId}

      this[ContentActions.MOVE_ENTRIES](params)
        .then(() => {
          this.clearSelection()
        })
        .catch((error) => {
          this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t(`move_failed`))
          this.$emit(`move-error`, error)
        })
    },

    removeAction() {
      this.$refs.modalRemove.openModal()
    },

    async downloadAction() {
      if (!this.parentId) {
        return
      }

      const bgActionId = `bg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
      // Subscribe before making the request so an exceptionally fast ZIP job
      // cannot complete before the HTTP request resolves.
      this.pendingDownloadActions.add(bgActionId)

      try {
        await this[ContentActions.SHARE_MULTI_NODE_DOWNLOAD]({
          parentNodeId: this.parentId,
          nodeIds: this.selectedItems.map(item => item.node_id),
          bgActionId,
          zipName: this.getMultiDownloadZipName(),
        })
      } catch (error) {
        this.pendingDownloadActions.delete(bgActionId)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || error.message)
      }
    },

    handleDownloadBgAction(message) {
      const bgActionId = message.bgActionId ?? message.bg_action_id

      const shareDetails = this.pendingShareActions.get(bgActionId)
      if (!this.pendingDownloadActions.has(bgActionId) && !shareDetails) {
        return
      }

      const status = String(message.status).toLowerCase()
      if (status === 'error') {
        this.pendingDownloadActions.delete(bgActionId)
        this.pendingShareActions.delete(bgActionId)
        return
      }

      if (!['done', 'success'].includes(status)) {
        return
      }

      const downloadUrl = this.getDownloadUrl(message)
      if (!downloadUrl) {
        return
      }

      // Remove the action before opening the link: socket events may be replayed.
      this.pendingDownloadActions.delete(bgActionId)

      if (shareDetails) {
        this.pendingShareActions.delete(bgActionId)
        this.sendZipShareMessage(downloadUrl, shareDetails)
        return
      }

      const link = document.createElement('a')
      link.target = '_blank'
      link.href = downloadUrl
      link.click()
    },

    getMultiDownloadZipName() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`
      const parentFolderName = this.breadcrumb[0]?.name || 'download'

      return `${timestamp}_${parentFolderName}_${this.selectedItems.length}_files.zip`
    },

    getDownloadUrl(message) {
      if (typeof message.text !== 'string') {
        return null
      }

      try {
        const { download_url: downloadUrl } = JSON.parse(message.text)
        if (typeof downloadUrl !== 'string') {
          return null
        }

        // The backend currently returns the URL rendered as a Markdown link.
        const markdownUrl = downloadUrl.match(/^\[[\s\S]*\]\((https?:\/\/[^)]+)\)$/)
        return new URL(markdownUrl ? markdownUrl[1] : downloadUrl).toString()
      } catch (error) {
        console.error('[share-download] unable to parse download URL', { message, error })
        return null
      }
    },

    openShareModal() {
      this.$refs.shareModal.open(this.selectedItems, {
        downloadName: this.getMultiDownloadZipName(),
      })
    },

    async shareAction({ items, emails, subject, message, expirationDate, downloadName }) {
      if (!this.parentId) {
        return
      }

      const bgActionId = `bg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
      this.pendingShareActions.set(bgActionId, {
        emails,
        subject,
        message,
        title: downloadName,
      })

      try {
        await this[ContentActions.SHARE_MULTI_NODE_DOWNLOAD]({
          parentNodeId: this.parentId,
          nodeIds: items.map(item => item.node_id),
          expirationDate,
          bgActionId,
          zipName: downloadName,
        })
      } catch (error) {
        this.pendingShareActions.delete(bgActionId)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || error.message)
      }
    },

    sendZipShareMessage(downloadUrl, { emails, subject, message, title }) {
      const options = {
        subject: subject || `[${this.$t('share_file_email_subject')}: ${title}]`,
        content: `${message || ''}<br><a href="${downloadUrl}">${title}</a><br><br>`,
        emails,
      }

      this[NotificationActions.SEND_SHARE_MESSAGE](options)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('send_mail_successfully'))
        })
        .catch(() => {
          this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('send_mail_error'))
        })
    },

    removeExecute() {
      this.$refs.modalRemove.closeModal()

      let nodesIds = this.selectedItems.map(item => item.node_id)
      let params = {'data': {'entries': nodesIds}}

      this[ContentActions.REMOVE_ENTRIES](params)
        .then(() => {
          this.$emit('remove-success')
          this.clearSelection()
        })
        .catch((error) => {
          if (error.response) {
            const responseData = error.response.data
            this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('error ') + responseData.message)
          } else {
            this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
          }
          this.$emit('remove-error')
        })
    },

  }
};
</script>
